import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Globals } from '../_globals/globals';
import {
  AssistantUpdatesService,
  AssistantUserUpdate
} from './assistant-updates.service';

interface AssistantSource {
  id: string;
  title: string;
}

interface AssistantResource {
  id: string;
  title: string;
  kind: 'download';
  filename: string;
}

interface AssistantAnswer {
  answer: string;
  conversationId?: string;
  sources: AssistantSource[];
  resources?: AssistantResource[];
  confidence: 'high' | 'medium' | 'insufficient';
  needsReview: boolean;
  interactionId?: string;
  reviewRecorded?: boolean;
  proposedAction?: AssistantProposedAction;
}

interface AssistantProposedAction {
  proposalId: string;
  signature: string;
  type: string;
  title: string;
  requiresConfirmation: boolean;
  payload: any;
}

interface AssistantTurn {
  question: string;
  answer: AssistantAnswer;
}

@Component({
  selector: 'app-assistant-mascot',
  templateUrl: './assistant-mascot.component.html',
  styleUrls: ['./assistant-mascot.component.css']
})
export class AssistantMascotComponent implements OnInit, OnDestroy {

  isGreeting = false;
  isOpen = false;
  activeTab: 'question' | 'updates' = 'question';
  question = '';
  answer: AssistantAnswer = null;
  conversationId = '';
  conversation: AssistantTurn[] = [];
  pendingQuestion = '';
  loading = false;
  error = '';
  feedback: 'helpful' | 'not_helpful' | null = null;
  feedbackLoading = false;
  feedbackError = '';
  resourceLoadingId = '';
  resourceError = '';
  actionLoadingId = '';
  actionError = '';

  readonly examples = [
    'Ayúdame a preparar un lote de producción con pedidos pendientes compatibles por tiempo.',
    '¿Por qué el listado PDF del plano de corte dice PRUEBA?',
    '¿Cómo funciona el parámetro Libertad y para qué sirve?',
    '¿Cuál es el método recomendado para imprimir el plano de corte?'
  ];

  private greetingTimer: number;
  private nextGreetingTimer: number;

  constructor(
    private http: HttpClient,
    private globals: Globals,
    public router: Router,
    public assistantUpdates: AssistantUpdatesService
  ) {}

  ngOnInit(): void {
    this.assistantUpdates.load(true);
    this.scheduleGreeting(4500);
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.greetingTimer);
    window.clearTimeout(this.nextGreetingTimer);
  }

  greet(): void {
    window.clearTimeout(this.greetingTimer);
    window.clearTimeout(this.nextGreetingTimer);
    this.isGreeting = true;
    this.greetingTimer = window.setTimeout(() => {
      this.isGreeting = false;
      this.scheduleGreeting();
    }, 1700);
  }

  openAssistant(tabIn: 'question' | 'updates' = 'question'): void {
    this.greet();
    this.isOpen = true;
    this.activeTab = tabIn;
    this.assistantUpdates.load(true);
    if (tabIn === 'question') {
      window.setTimeout(() => {
        const field = document.getElementById('dimpro-assist-question-modal') as HTMLElement;
        if (field && typeof field.focus === 'function') {
          field.focus();
        }
      }, 100);
    }
  }

  closeAssistant(): void {
    this.isOpen = false;
  }

  selectTab(tabIn: 'question' | 'updates'): void {
    this.activeTab = tabIn;
    if (tabIn === 'updates') {
      this.assistantUpdates.load(true);
    }
  }

  askAssistant(): void {
    const question = (this.question || '').trim();
    if (this.loading || question.length < 3) {
      return;
    }

    this.loading = true;
    this.pendingQuestion = question;
    this.question = '';
    this.error = '';
    this.resetFeedback();
    this.scrollConversation();

    this.http.post<AssistantAnswer>(
      this.globals.apiUrl + '/help/ask',
      {
        question,
        conversationId: this.conversationId || undefined,
        context: {
          view: this.viewName(),
          route: this.router.url || '/'
        }
      }
    ).subscribe(
      response => {
        this.loading = false;
        this.pendingQuestion = '';
        this.answer = response;
        this.conversationId = response.conversationId || this.conversationId;
        this.conversation.push({ question, answer: response });
        this.assistantUpdates.load(true);
        this.focusQuestion();
      },
      error => {
        this.loading = false;
        this.pendingQuestion = '';
        this.question = question;
        this.error = this.getErrorMessage(
          error,
          'No ha sido posible consultar a Dimpro Assist.'
        );
      }
    );
  }

  useExample(questionIn: string): void {
    if (this.loading) {
      return;
    }
    this.question = questionIn;
    this.error = '';
  }

  newConversation(): void {
    if (this.loading) {
      return;
    }
    this.question = '';
    this.answer = null;
    this.conversationId = '';
    this.conversation = [];
    this.pendingQuestion = '';
    this.error = '';
    this.resetFeedback();
    this.focusQuestion();
  }

  sendFeedback(helpfulIn: boolean): void {
    if (
      !this.answer ||
      !this.answer.interactionId ||
      this.feedbackLoading ||
      this.feedback
    ) {
      return;
    }

    this.feedbackLoading = true;
    this.feedbackError = '';
    this.http.post<any>(
      this.globals.apiUrl + '/help/feedback',
      {
        interactionId: this.answer.interactionId,
        helpful: helpfulIn
      }
    ).subscribe(
      () => {
        this.feedbackLoading = false;
        this.feedback = helpfulIn ? 'helpful' : 'not_helpful';
        if (!helpfulIn && this.answer) {
          this.answer.reviewRecorded = true;
        }
      },
      error => {
        this.feedbackLoading = false;
        this.feedbackError = this.getErrorMessage(
          error,
          'No ha sido posible registrar tu valoración.'
        );
      }
    );
  }

  markUpdateRead(updateIn: AssistantUserUpdate): void {
    this.assistantUpdates.markRead(updateIn);
  }

  updateKindLabel(updateIn: AssistantUserUpdate): string {
    if (!updateIn) {
      return 'Novedad';
    }
    if (updateIn.kind === 'bug_fixed') {
      return 'Corrección';
    }
    if (updateIn.kind === 'feature_available') {
      return 'Nueva función';
    }
    return 'Documentación';
  }

  confidenceLabel(confidenceIn: string): string {
    if (confidenceIn === 'high') {
      return 'Confianza alta';
    }
    if (confidenceIn === 'medium') {
      return 'Confianza media';
    }
    return 'Pendiente de revisión';
  }

  prepareProductionLot(actionIn: AssistantProposedAction): void {
    if (!actionIn || actionIn.type !== 'propose_production_lot' || this.actionLoadingId) {
      return;
    }
    this.actionLoadingId = actionIn.proposalId;
    this.actionError = '';
    this.http.post<any>(
      this.globals.apiUrl + '/help/action/production-lot/prepare',
      { proposedAction: actionIn }
    ).subscribe(
      response => {
        this.actionLoadingId = '';
        if (!response || response.ok !== true || !Array.isArray(response.positions)) {
          this.actionError = 'La propuesta no contiene posiciones que se puedan revisar.';
          return;
        }
        window.sessionStorage.setItem(
          'dimpro_assist_production_lot',
          JSON.stringify(response)
        );
        this.closeAssistant();
        if ((this.router.url || '').split('?')[0] === '/production_planning') {
          window.dispatchEvent(new CustomEvent('dimpro-assist-production-lot'));
        } else {
          this.router.navigate(['/production_planning']);
        }
      },
      error => {
        this.actionLoadingId = '';
        this.actionError = this.getErrorMessage(
          error,
          'No ha sido posible validar la propuesta de lote.'
        );
      }
    );
  }

  trackUpdate(indexIn: number, itemIn: AssistantUserUpdate): number {
    return itemIn ? itemIn.id : indexIn;
  }

  trackSource(indexIn: number, itemIn: AssistantSource): string {
    return itemIn && itemIn.id ? itemIn.id : indexIn.toString();
  }

  trackResource(indexIn: number, itemIn: AssistantResource): string {
    return itemIn && itemIn.id ? itemIn.id : indexIn.toString();
  }

  trackTurn(indexIn: number, itemIn: AssistantTurn): string {
    return itemIn && itemIn.answer && itemIn.answer.interactionId
      ? itemIn.answer.interactionId
      : indexIn.toString();
  }

  downloadResource(resourceIn: AssistantResource): void {
    if (
      !resourceIn ||
      !resourceIn.id ||
      resourceIn.kind !== 'download' ||
      this.resourceLoadingId
    ) {
      return;
    }

    this.resourceLoadingId = resourceIn.id;
    this.resourceError = '';
    this.http.get(
      this.globals.apiUrl + '/help/resource/' + encodeURIComponent(resourceIn.id),
      { responseType: 'blob' }
    ).subscribe(
      blob => {
        this.resourceLoadingId = '';
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = this.safeFilename(resourceIn.filename);
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(objectUrl);
      },
      error => {
        this.resourceLoadingId = '';
        this.resourceError = this.getErrorMessage(
          error,
          'No ha sido posible descargar el recurso.'
        );
      }
    );
  }

  private viewName(): string {
    const route = (this.router.url || '/').split('?')[0].split('#')[0];
    const parts = route.split('/').filter(part => Boolean(part));
    return parts.length > 0 ? parts[0] : 'inicio';
  }

  private getErrorMessage(errorIn: any, fallbackIn: string): string {
    if (errorIn && errorIn.error && errorIn.error.message) {
      return errorIn.error.message;
    }
    return fallbackIn;
  }

  private resetFeedback(): void {
    this.feedback = null;
    this.feedbackLoading = false;
    this.feedbackError = '';
    this.resourceLoadingId = '';
    this.resourceError = '';
    this.actionLoadingId = '';
    this.actionError = '';
  }

  private focusQuestion(): void {
    window.setTimeout(() => {
      this.scrollConversationNow();
      const field = document.getElementById('dimpro-assist-question-modal') as HTMLElement;
      if (field && typeof field.focus === 'function') {
        field.focus();
      }
    }, 100);
  }

  private scrollConversation(): void {
    window.setTimeout(() => this.scrollConversationNow(), 0);
  }

  private scrollConversationNow(): void {
    const conversation = document.getElementById('dimpro-assist-conversation-modal');
    if (conversation) {
      conversation.scrollTop = conversation.scrollHeight;
    }
  }

  private safeFilename(filenameIn: string): string {
    const filename = (filenameIn || '').replace(/[^a-zA-Z0-9._-]+/g, '_');
    return filename || 'recurso_dimpro';
  }

  private scheduleGreeting(delayIn?: number): void {
    window.clearTimeout(this.nextGreetingTimer);
    const delay = delayIn || (16000 + Math.round(Math.random() * 8000));
    this.nextGreetingTimer = window.setTimeout(() => this.greet(), delay);
  }
}
